import { memo, useMemo, useState } from 'react';
import { Accordion, AccordionItem, TableCell, Tag, TextInput } from '@carbon/react';
import { Launch } from '@carbon/react/icons';
import cx from 'clsx';
import _ from 'lodash';

import { useIsReadonly } from 'core/readonly';
import OutputsSpacesTable from 'features/outputs/components/OutputsSpacesTable';
import { SPACE_TYPE_LABEL } from 'models/spaces';
import ModifiedValue from 'shared/components/ModifiedValue';
import { formatArea, formatNumeric } from 'utils/format';
import styles from 'theme/features/outputs/specialty-spaces-accordion.module.scss';

const SpecialtySpacesTable = ({
  rooms,
  rowEdits,
  onRowEdit,
  isShowingCustomizations,
  isExport = false,
  project,
  workstations
}) => {
  const isReadonly = useIsReadonly();

  const data = rooms.map((room) => {
    const edits = rowEdits[room.id]?.value ?? {};

    const isExcluded = edits?.isExcluded;
    const hasDefaultQuantity = room.defaultQuantity != null;
    const quantity = isExcluded ? 0 : edits.quantity ?? room.defaultQuantity;

    return {
      id: room.id.toString(),
      name: room.name,
      type: room.type == null ? null : SPACE_TYPE_LABEL[room.type],
      seats: <ModifiedValue isModified={edits.seats != null}>{formatNumeric(edits.seats)}</ModifiedValue>,
      unitArea: <ModifiedValue isModified={edits.unitArea != null}>{formatNumeric(edits.unitArea)}</ModifiedValue>,
      quantity: (
        <ModifiedValue
          isModified={
            isExcluded ||
            (edits.quantity != null && (!hasDefaultQuantity || Number(edits.quantity) !== room.defaultQuantity))
          }
        >
          {formatNumeric(quantity)}
        </ModifiedValue>
      ),
      totalSeats: formatNumeric(edits.seats != null && quantity != null ? edits.seats * quantity : 0),
      totalArea: formatNumeric(edits.unitArea != null && quantity != null ? edits.unitArea * quantity : 0)
    };
  });

  const headers = [
    { key: 'name', header: 'Space Name', width: '20%' },
    { key: 'type', header: 'Type', width: '15%' },
    { key: 'seats', header: 'Seats', width: '10%' },
    { key: 'unitArea', header: 'Unit Area', width: '10%' },
    { key: 'quantity', header: 'Quantity', width: '10%' },
    { key: 'totalSeats', header: 'Total Seats', width: '10%' },
    { key: 'totalArea', header: 'Total Area', width: '10%' }
  ];

  const roomById = useMemo(() => _.keyBy(rooms, 'id'), [rooms]);

  const renderEditRow = ({ id, rowEdits, onEdit }) => {
    const room = roomById[id];

    return (
      <>
        <TableCell />
        <TableCell />
        <TableCell>
          <TextInput
            id={`seats-edit-${id}`}
            type="number"
            labelText="Custom Seats"
            placeholder={isReadonly ? '-' : 'Seats'}
            onChange={(e) => onEdit('seats', e.target.value)}
            value={rowEdits?.seats ?? ''}
            hideLabel
            min={0}
            readOnly={isReadonly}
          />
        </TableCell>
        <TableCell>
          <TextInput
            id={`unit-area-edit-${id}`}
            type="number"
            labelText="Custom Unit Area"
            placeholder={isReadonly ? '-' : 'Area'}
            onChange={(e) => onEdit('unitArea', e.target.value)}
            value={rowEdits?.unitArea ?? ''}
            hideLabel
            min={0}
            readOnly={isReadonly}
          />
        </TableCell>
        <TableCell>
          <TextInput
            id={`quantity-edit-${id}`}
            type="number"
            labelText="Custom Quantity"
            placeholder={isReadonly ? '-' : 'Quantity'}
            onChange={(e) => onEdit('quantity', e.target.value)}
            value={rowEdits?.quantity ?? room?.defaultQuantity?.toString() ?? ''}
            hideLabel
            readOnly={isReadonly}
            min={0}
          />
        </TableCell>
        <TableCell colSpan={2}>
          <TextInput
            id={`quantity-edit-${id}`}
            labelText="Notes"
            placeholder={isReadonly ? '-' : 'Add notes'}
            onChange={(e) => onEdit('notes', e.target.value)}
            value={rowEdits?.notes ?? ''}
            readOnly={isReadonly}
            hideLabel
          />
        </TableCell>
      </>
    );
  };

  return (
    <OutputsSpacesTable
      isShowingCustomizations={isShowingCustomizations}
      data={data}
      headers={headers}
      outputs={{}}
      renderEditRow={renderEditRow}
      rowEdits={rowEdits}
      onRowEdit={onRowEdit}
      isExport={isExport}
      project={project}
      workstations={workstations}
    />
  );
};

const SpecialtySpacesAccordion = ({
  spaces,
  rooms,
  projectSpecialtySpaces,
  rowEdits,
  onRowEdit,
  isShowingCustomizations,
  measurementSystem,
  isExport = false,
  project,
  workstations
}) => {
  const [openedItemIds, setOpenedItemIds] = useState([]);

  const roomsBySpace = useMemo(() => _.groupBy(rooms, 'specialtySpaceId'), [rooms]);
  const projectSpecialtySpaceById = useMemo(
    () => _.keyBy(projectSpecialtySpaces, 'specialtySpaceId'),
    [projectSpecialtySpaces]
  );

  return (
    <Accordion>
      {spaces?.map(({ id, name }) => {
        const rooms = roomsBySpace[id];
        const hasRooms = !_.isEmpty(rooms);
        const projectConfiguration = projectSpecialtySpaceById[id];

        const isIncluded = !!projectConfiguration;
        const inclusionTitle = isIncluded ? 'Included' : 'Excluded';

        const isOpened = openedItemIds.includes(id) || (isExport && isIncluded);

        return (
          <AccordionItem
            key={id}
            className={cx(styles.item, !hasRooms && styles['item--disabled'], isOpened && styles['item--expanded'])}
            open={isOpened}
            disabled={!isIncluded}
            onHeadingClick={() =>
              hasRooms && setOpenedItemIds(isOpened ? _.without(openedItemIds, id) : [...openedItemIds, id])
            }
            title={
              <div className={styles['item__title']}>
                <div className={styles['item__title-header']}>
                  <Tag type={isIncluded ? 'green' : 'red'} size="sm" title={inclusionTitle}>
                    {inclusionTitle}
                  </Tag>
                  <a
                    href="https://www.ibm.com/design/workplace/space-types/specialty-space-types"
                    target="_blank"
                    rel="noreferrer"
                    className={styles['item__name']}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {name}
                    <span className={styles['item__name-link']}>
                      &#xfeff;
                      <Launch size={14} />
                    </span>
                  </a>
                </div>
                <span className={styles['item__value']}>
                  Total Estimated Area:{' '}
                  <strong>{formatArea(projectConfiguration?.area, measurementSystem) ?? 'Unknown'}</strong>
                </span>
              </div>
            }
          >
            {isOpened && hasRooms && (
              <SpecialtySpacesTable
                isShowingCustomizations={isShowingCustomizations}
                rooms={rooms}
                rowEdits={rowEdits}
                onRowEdit={onRowEdit}
                isExport={isExport}
                project={project}
                workstations={workstations}
              />
            )}
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};

export default memo(SpecialtySpacesAccordion);
